import { FieldStatus } from '@/classes/FieldStatus';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { EditionModeRef } from '@/classes/ScenarioReference';
import { InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import { axiosWrapper } from '@/services/axiosWrapper';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
  IReport,
  ITechnicalResult,
  RenFrReport
} from '@/classes/TechnicalResultsInterface';
import { ProjectMeta } from '@/classes/ProjectMeta';
import { ScenarioSizingExt } from '@/classes/scenario/sizing/ScenarioSizingExt';
import { DtoExt } from '@/classes/scenario/DtoExt';
import { Scenario } from '@/dto/scenario/Scenario';
import { DispatchOptions, Payload } from 'vuex';
import { Invalidation } from '@/classes/rules/Invalidation';
import { PVPayloads } from '@/store/modules/steps/pv';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import VueI18n, { LocaleMessages } from 'vue-i18n';
import { Path, PathWithValue } from '@/helpers/pathHelper';
import {
  References,
  SurfaceDefinitionExt
} from '@/classes/scenario/pv/SurfaceDefinitionExt';
import { ScenarioRenFr } from '@/dto/scenario/ren-fr/ScenarioRenFr';

const defaultValue = new ScenarioExt();
defaultValue.injectionModelRef =
  InjectionModelRef.self_consumption_with_injection;

interface RuleHelper {
  getDefaultScenario: () => ScenarioExt;
  cleanScenario: (scenario: ScenarioExt, fieldStatuses: FieldStatus[]) => void;
  getDtoFieldStatuses: (scenario: ScenarioExt, path: string[]) => FieldStatus[];
}

interface Network {
  get: <T>(url: string) => Promise<{ data: T }>;
  post: <T>(url: string, body: object) => Promise<{ data: T }>;
  put: <T>(url: string, body: object) => Promise<{ data: T }>;
}

interface Dependencies {
  http: Network;
  ruleHelper: RuleHelper;
  loadFile: (filename: string) => Promise<{ data: unknown }>;
  getPaths: (currentObject: unknown, currentPath?: Path) => PathWithValue[];
  assignToPath: (
    target: unknown,
    path: Path,
    value: unknown,
    createMissingValues?: boolean
  ) => boolean;
  refreshLocaleMessages: (localeMessages: LocaleMessages) => void;
  i18n: VueI18n;
  errorHandler: (error: unknown) => void;
}

class SaveFields implements Payload {
  public readonly type = 'scenario/saveFields';
  public scenario: ScenarioExt;
  public invalidation?: Invalidation;

  constructor(options: Omit<SaveFields, 'type'>) {
    this.scenario = options.scenario;
    this.invalidation = options.invalidation;
  }

  public static buildPayload(
    options: Omit<SaveFields, 'type'>
  ): [SaveFields, DispatchOptions] {
    return [new SaveFields(options), { root: true }];
  }
}

export const ScenarioPayloads = {
  SaveFields
};

export const buildScenarioModule = ({
  http,
  ruleHelper,
  loadFile,
  getPaths,
  assignToPath,
  refreshLocaleMessages,
  i18n,
  errorHandler
}: Dependencies) => {
  @Module({
    namespaced: true
  })
  class ScenarioModule extends VuexModule {
    private fields: ScenarioExt = new ScenarioExt(defaultValue);

    private editionMode: EditionModeRef = EditionModeRef.creation;

    private algoResultMap: Map<string, IReport | RenFrReport> = new Map();

    private reloadTrigger: any = {};

    private duplicatedScenarios: Map<string, ScenarioExt> = new Map();

    public isSaving: boolean = false;

    public isMailChecking: boolean = false;

    private invalidMails: string[] | null = null;

    private scenarioShared: boolean = false;

    public algoIsRunning: boolean = false;
    // LOCAL ACTIONS =============================================================
    @Action({ rawError: true })
    async loadFieldLocales(projectType: ProjectTypeRef) {
      try {
        const { data } = await loadFile(`${projectType}_locales`);
        const messages: LocaleMessages = i18n.messages;
        getPaths(data).forEach((fieldLocale) => {
          try {
            assignToPath(messages, fieldLocale.path, fieldLocale.value, true);
          } catch (error) {
            errorHandler(error);
          }
        });
        refreshLocaleMessages(messages);
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public async saveFields(payload: SaveFields): Promise<ScenarioExt> {
      const newScenario = new ScenarioExt(payload.scenario);
      this.context.commit('setFields', newScenario);
      await payload.invalidation?.invalidate({
        invalidateReports: () => this.context.dispatch('invalidAlgoResults'),
        invalidateSizing: () =>
          this.context.dispatch('sizing/invalidSizingById', undefined, {
            root: true
          }),
        invalidatePreSizing: () =>
          this.context.dispatch('preSizing/invalidate', undefined, {
            root: true
          }),
        invalidatePVCurve: () =>
          this.context.dispatch(...PVPayloads.InvalidateCurve.buildPayload())
      });
      return newScenario;
    }

    @Action
    public invalidAlgoResults(scenarioId: string | null = this.getFields.uuid) {
      const payload = { id: scenarioId, data: null };
      this.context.commit('setAlgoResult', payload);
      this.context.dispatch('sensitivityAnalysis/invalidate', undefined, {
        root: true
      });
    }

    // SCENARIO API =============================================================
    @Action
    public checkMail() {
      // nothing atm
      this.context.commit('setIsRunningState', true);
      setTimeout(() => {
        this.context.commit('setIsRunningState', false);
      }, 3000);
    }

    @Action
    public shareScenario(payload: { uuid: string; emails: string[] }) {
      this.context.commit('setInvalidMails', null);
      axiosWrapper
        .postToApi(`/scenario/${payload.uuid}/share`, {
          emails: payload.emails
        })
        .then(() => {
          this.context.commit('setScenarioShared', true);
          this.context.commit('setInvalidMails', []);
        })
        .catch((err) => {
          if (err && err.response && err.response.status === 400) {
            // invalid mails
            this.context.commit(
              'setInvalidMails',
              err.response && err.response.data && err.response.data.emails
            );
          }
        });
    }

    @Action({ commit: 'setFields' })
    public setInvalidEmails(payload: string[] | null) {
      this.context.commit('setInvalidMails', payload);
    }

    // Creates Scenario
    /* the api return the cenario id which we ll use to save our new
    scenario */
    @Action({ rawError: true })
    public async createScenario(projectId: string): Promise<void> {
      const projectList: ProjectMeta[] =
        this.context.rootGetters['structure/getProjects'];
      const project: ProjectMeta | undefined = projectList.find(
        ({ uuid }) => uuid === projectId
      );
      if (project === undefined) {
        const err = `Won't create scenario: can't find project with id ${projectId}`;
        errorHandler(err);
        return Promise.reject(err);
      }
      try {
        await this.context.dispatch('project/setCurrentProject', project, {
          root: true
        });
        const defaultScenario =
          this.context.rootGetters['scenarioRules/defaultScenario']();
        const { data } = await http.put<Scenario>(
          `/project/${projectId}/scenario`,
          defaultScenario.toDto()
        );
        const newScenario = new ScenarioExt(defaultScenario);
        newScenario.uuid = data.uuid ?? null;
        newScenario.project = project;
        this.context.commit('setFields', newScenario);
        this.context.commit('setEditionMode', EditionModeRef.creation);
        this.context.commit('setReloadTrigger');
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public duplicateScenario(payload: {
      scenarioId: string;
      projectId: string;
    }): Promise<any> {
      let duplicatedPayload: ScenarioExt;
      return axiosWrapper
        .getFromApi(`/scenario/${payload.scenarioId}`)
        .then((res: any) => {
          duplicatedPayload = ScenarioExt.fromDto(res.data);
          duplicatedPayload.name = duplicatedPayload.name + ' copy';
          duplicatedPayload.uuid = null;
          duplicatedPayload.isRenFrance = res.data?.isRenFrance ?? false;

          return axiosWrapper.putOnApi(
            `/project/${payload.projectId}/scenario`,
            duplicatedPayload.toDto()
          );
        })
        .then((res: any) => {
          duplicatedPayload.uuid = res.data.uuid;
          this.context.commit('setDuplicatedScenarios', {
            id: duplicatedPayload.uuid,
            data: duplicatedPayload
          });
          return this.context.dispatch('updateScenario', duplicatedPayload);
        })
        .catch(errorHandler);
    }

    /* Care, we 'manually' add the scenarioId to our Scenario instance */
    @Action({ rawError: true })
    public async loadScenario(scenarioId: string): Promise<void> {
      if (!scenarioId) {
        errorHandler(`Trying to load scenario with invalid id ${scenarioId}`);
        return Promise.resolve();
      }
      // reset the duplicatedScenario status
      this.context.commit('setDuplicatedScenarios', {
        id: scenarioId,
        data: null
      });
      // find project from scenario Id
      const projectList: ProjectMeta[] =
        this.context.rootGetters['structure/getProjects'];
      const project: ProjectMeta | undefined = projectList.find(
        (project) =>
          project.scenarios?.find(({ uuid }) => uuid === scenarioId) !==
          undefined
      );
      if (project === undefined) {
        const err = `Won't load scenario: can't find project of scenario with id ${scenarioId}`;
        errorHandler(err);
        return Promise.reject(err);
      }
      try {
        const { data } = await http.get<Scenario | ScenarioRenFr>(
          `/scenario/${scenarioId}`
        );
        await this.context.dispatch('project/setCurrentProject', project, {
          root: true
        });
        const surfaceDefinitionReferences =
          this.context.rootGetters['references/surfaceDefinition'];
        this.context.commit(
          'setFields',
          readScenario(data, scenarioId, project, surfaceDefinitionReferences)
        );
        this.context.commit('setEditionMode', EditionModeRef.edition);
        this.context.dispatch('sizing/invalidSizingById', undefined, {
          root: true
        });
        this.context.dispatch('preSizing/invalidate', undefined, {
          root: true
        });
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    loadAlgoByScenarioId(scenarioId: string) {
      return axiosWrapper
        .getFromApi(`/algo/report/${scenarioId}`)
        .then((response) => {
          if (response && response.data) {
            this.context.commit('setAlgoResult', {
              id: this.getFields.uuid,
              data: response.data
            });
          }
        })
        .catch(errorHandler);
    }

    @Action
    public updateScenario(scenarioPayload: ScenarioExt): Promise<any> {
      if (!scenarioPayload.uuid) {
        errorHandler(
          `Trying to update scenario with invalid id ${scenarioPayload.uuid}`
        );
        return Promise.resolve();
      }
      this.context.commit('setIsSaving', true);
      return axiosWrapper
        .postToApi(`/scenario/${scenarioPayload.uuid}`, scenarioPayload.toDto())
        .then(() => {
          this.context.commit('setReloadTrigger');
          this.context.commit('setIsSaving', false);
        })
        .catch((e) => {
          this.context.commit('setIsSaving', false);
          errorHandler(e);
        });
    }

    @Action
    public deleteScenario(scenarioId: any): Promise<any> {
      if (!scenarioId) {
        errorHandler(`Trying to delete scenario with invalid id ${scenarioId}`);
        return Promise.resolve();
      }
      // reset the duplicatedScenario status
      if (this.duplicatedScenarios.get(scenarioId) instanceof ScenarioExt) {
        this.context.commit('setDuplicatedScenarios', {
          id: scenarioId,
          data: null
        });
      }
      return axiosWrapper
        .deleteFromApi(`/scenario/${scenarioId}`, {})
        .then(() => {
          this.context.commit('setReloadTrigger');
        })
        .catch(errorHandler);
    }

    @Action
    public async sendScenarioToAlgorithm(): Promise<void> {
      if (this.getCriticalFieldErrors.length) {
        errorHandler('Payload has errors and won\'t be sent');
        return;
      }
      this.context.commit('setAlgoIsRunning', true);
      const { uuid, sizing } = this.fields.toDto();
      try {
        const { data } = await http.post<IReport>('/algo/executeV3', {
          uuid,
          sizing
        });
        this.context.dispatch('report/resetLocalReport', null, {
          root: true
        });
        this.context.commit('setAlgoResult', {
          id: uuid,
          data
        });
      } catch (error) {
        errorHandler(error);
      } finally {
        this.context.commit('setAlgoIsRunning', false);
      }
    }

    @Action
    public async sendScenarioToRenFrAlgorithm(): Promise<void> {
      if (this.getCriticalFieldErrors.length) {
        errorHandler('Payload has errors and won\'t be sent');
        return;
      }
      this.context.commit('setAlgoIsRunning', true);
      const { uuid } = this.fields.toDto();
      try {
        const { data } = await http.post<RenFrReport>('/algo/executeRenFr', {
          uuid
        });

        this.context.commit('setRenFrAlgoResult', {
          id: uuid,
          data
        });
      } catch (error) {
        errorHandler(error);
      } finally {
        this.context.commit('setAlgoIsRunning', false);
      }
    }

    // MUTATIONS =============================================================
    @Mutation
    private setInvalidMails(payload: string[]) {
      this.invalidMails = payload;
    }

    @Mutation
    public setScenarioShared(bool: boolean) {
      this.scenarioShared = bool;
    }

    @Mutation
    private setIsRunningState(payload: boolean) {
      this.isMailChecking = payload;
    }

    @Mutation
    private setFields(payload: ScenarioExt) {
      this.fields = payload;
    }
    @Mutation
    private setEditionMode(payload: EditionModeRef) {
      this.editionMode = payload;
    }
    @Mutation
    private setReloadTrigger() {
      this.reloadTrigger = {};
    }
    @Mutation
    private setAlgoResult(payload: { id: string; data: IReport }) {
      this.algoResultMap.set(payload.id, payload.data);
      this.algoResultMap = new Map(this.algoResultMap);
    }
    @Mutation
    private setRenFrAlgoResult(payload: { id: string; data: RenFrReport }) {
      this.algoResultMap.set(payload.id, payload.data);
      this.algoResultMap = new Map(this.algoResultMap);
    }
    @Mutation
    private setDuplicatedScenarios(payload: {
      id: string;
      data: ScenarioExt | null;
    }) {
      if (payload.data instanceof ScenarioExt) {
        this.duplicatedScenarios.set(payload.id, payload.data);
      } else if (this.duplicatedScenarios.has(payload.id)) {
        this.duplicatedScenarios.delete(payload.id);
      }
      this.duplicatedScenarios = new Map(this.duplicatedScenarios);
    }
    @Mutation
    private setIsSaving(bool: boolean) {
      this.isSaving = bool;
    }
    @Mutation
    private setAlgoIsRunning(bool: boolean) {
      this.algoIsRunning = bool;
    }

    get getInvalidMails(): string[] | null {
      return this.invalidMails;
    }

    get getScenarioShared(): boolean {
      return this.scenarioShared;
    }

    get getIsMailChecking(): boolean {
      return this.isMailChecking;
    }

    get getAlgoIsRunning(): boolean {
      return this.algoIsRunning;
    }
    get getFields(): ScenarioExt {
      return new ScenarioExt(this.fields);
    }
    // Field statuses
    get getFieldStatuses(): FieldStatus[] {
      // 40 - 50 ms for this function
      return ruleHelper.getDtoFieldStatuses(this.getFields, []);
    }
    get getFieldErrors(): FieldStatus[] {
      return this.getFieldStatuses.filter((s) => !s.isValid());
    }
    get getCriticalFieldErrors() {
      return this.getFieldErrors.filter((err) => {
        return err.path[0] !== 'dev';
      });
    }

    // Front settings
    get getEditionMode(): EditionModeRef {
      return this.editionMode;
    }
    get getReloadTrigger(): boolean {
      return this.reloadTrigger;
    }

    // Algorithm result
    get getAlgoResult(): IReport | null {
      return (
        (this.algoResultMap.get(this.getFields.uuid || '') as IReport) || null
      );
    }

    get getRenFrAlgoResult(): RenFrReport | null {
      return (
        (this.algoResultMap.get(this.getFields.uuid || '') as RenFrReport) ||
        null
      );
    }

    get hasTechnicalReport(): boolean {
      return (
        !this.getFields.hasInjectionType(InjectionModelRef.total_injection) &&
        this.getTechnicalResult !== null
      );
    }

    get getTechnicalResult(): ITechnicalResult | null {
      return this.getAlgoResult && this.getAlgoResult.technical;
    }

    get getEconomicalResult(): ITechnicalResult | null {
      return this.getAlgoResult && this.getAlgoResult.eco;
    }

    get getEconomicalRenFrResult(): RenFrReport | null {
      return this.getRenFrAlgoResult;
    }

    // Duplicated scenarios
    get getDuplicatedScenarios(): Map<string, ScenarioExt> {
      return this.duplicatedScenarios;
    }
    // saving state
    get getScenarioIsSaving(): boolean {
      return this.isSaving;
    }
  }

  function readScenario(
    data: Scenario | ScenarioRenFr,
    scenarioId: string,
    project: ProjectMeta,
    surfaceDefinitionReferences: References
  ): ScenarioExt {
    const scenario = ScenarioExt.fromDto(data);
    scenario.uuid = scenarioId;
    scenario.project = project;
    if (scenario.pv?.surfaceDefinition) {
      scenario.pv.surfaceDefinition = new SurfaceDefinitionExt(
        scenario.pv.surfaceDefinition,
        surfaceDefinitionReferences
      );
    }
    applyDefaultValueOnNewFields(scenario);
    resetSizingFields(scenario, data);
    return scenario;
  }

  function applyDefaultValueOnNewFields(scenario: ScenarioExt): void {
    const defaultScenario = ruleHelper.getDefaultScenario();
    DtoExt.recursiveApply(defaultScenario, scenario);
  }

  function resetSizingFields(
    scenario: ScenarioExt,
    data: Scenario | ScenarioRenFr
  ): void {
    // Other values are set when visiting sizing page based on data returned by /sizing/default api
    scenario.sizing = new ScenarioSizingExt({
      algorithmRef: data.sizing?.algorithmRef,
      hasBatteryRange: data.sizing?.hasBatteryRange,
      pvReport: data.sizing?.pvReport
    });
  }

  return ScenarioModule;
};
